import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import {
  Package,
  Network,
  BarChart,
  Zap,
  Shield,
  Globe,
  Database,
  Clock,
  Users,
  TrendingUp,
  Lock,
} from "lucide-react";

const FeaturesPage = () => {
  useSEO({
    title: 'Commerce Operations Platform Features | Ommni - Inventory, Orders & Analytics',
    description: 'Explore Ommni\'s powerful features: intelligent inventory management, omnichannel order orchestration, advanced analytics, enterprise security, and 100+ integrations for modern commerce.',
    keywords: 'commerce platform features, inventory management software, order management system, retail analytics, omnichannel commerce, enterprise security, commerce automation',
    canonical: 'https://ommni.io/features',
  });

  const features = [
    {
      icon: Package,
      title: "Intelligent Inventory Management",
      description:
        "Real-time visibility across all locations with AI-powered demand forecasting, automated replenishment, and smart allocation.",
      details: [
        "Multi-location inventory tracking",
        "Predictive stock recommendations",
        "Automated reorder points",
        "Inventory optimization algorithms",
      ],
    },
    {
      icon: Network,
      title: "Omnichannel Order Orchestration",
      description:
        "Seamlessly manage orders from any channel with intelligent routing, real-time updates, and automated fulfillment workflows.",
      details: [
        "Multi-channel order aggregation",
        "Smart order routing",
        "Real-time order tracking",
        "Automated fulfillment rules",
      ],
    },
    {
      icon: BarChart,
      title: "Advanced Analytics & Reporting",
      description:
        "Make data-driven decisions with comprehensive dashboards, custom reports, and actionable insights.",
      details: [
        "Real-time performance metrics",
        "Custom report builder",
        "Predictive analytics",
        "Export and API access",
      ],
    },
    {
      icon: Zap,
      title: "High-Performance Infrastructure",
      description:
        "Enterprise-grade platform that processes millions of transactions daily with 99.99% uptime guarantee.",
      details: [
        "Auto-scaling infrastructure",
        "Sub-second response times",
        "Global CDN deployment",
        "24/7 monitoring",
      ],
    },
    {
      icon: Globe,
      title: "Global Operations Support",
      description:
        "Manage international operations with multi-currency, multi-language, and regional compliance support.",
      details: [
        "Multi-currency support",
        "Localization & translations",
        "Tax compliance automation",
        "Regional warehousing",
      ],
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description:
        "Bank-level security with encryption at rest and in transit, and comprehensive access controls.",
      details: [
        "End-to-end encryption",
        "Role-based access control",
        "Regular security audits",
        "Advanced threat protection",
      ],
    },
    {
      icon: Database,
      title: "Unified Data Platform",
      description:
        "Centralize all your commerce data in one place with powerful APIs and data synchronization capabilities.",
      details: [
        "Real-time data sync",
        "RESTful API access",
        "Webhook support",
        "Data export tools",
      ],
    },
    {
      icon: Clock,
      title: "Automated Workflows",
      description:
        "Streamline operations with customizable automation rules, triggers, and business logic.",
      details: [
        "Custom workflow builder",
        "Conditional triggers",
        "Email notifications",
        "Integration with external systems",
      ],
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description:
        "Empower your team with role-based permissions, activity logs, and collaborative tools.",
      details: [
        "User management",
        "Audit trails",
        "Team messaging",
        "Task assignments",
      ],
    },
    {
      icon: TrendingUp,
      title: "Demand Forecasting",
      description:
        "Leverage machine learning to predict demand patterns and optimize inventory levels.",
      details: [
        "AI-powered predictions",
        "Seasonal trend analysis",
        "Historical data insights",
        "Custom forecasting models",
      ],
    },
    {
      icon: Lock,
      title: "Compliance & Governance",
      description:
        "Meet regulatory requirements with built-in compliance tools and comprehensive audit trails.",
      details: [
        "GDPR compliance",
        "Data retention policies",
        "Compliance reporting",
        "Access logs",
      ],
    },
  ];

  return (
    <div className="min-h-screen" itemScope itemType="https://schema.org/WebPage">
      <Navigation />
      <main className="pt-32 px-4 pb-24" role="main">
        <div className="container mx-auto">
          {/* Hero Section */}
          <header className="text-center mb-20">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Everything You Need for{" "}
              <span className="text-gradient">Modern Commerce</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Powerful features designed to help you manage complex commerce operations
              at scale. From inventory to analytics, we've got you covered.
            </p>
          </header>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={index}
                  className="p-8 gradient-card hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="p-3 rounded-lg bg-primary/10 w-fit mb-6 group-hover:bg-primary/20 transition-colors">
                    <Icon className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground mb-4">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.details.map((detail, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start">
                        <span className="text-primary mr-2">•</span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="relative overflow-hidden rounded-lg border-2 bg-card p-12 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-purple-500/5 to-background"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Transform Your Operations?
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join leading retailers who are already using Ommni to scale their commerce operations.
              </p>
              <Button variant="cta" size="lg" asChild>
                <Link to="/contact">Get Started</Link>
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Breadcrumbs />
      <Footer />
    </div>
  );
};

export default FeaturesPage;
